import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Upload, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bulk product CSV import.
 * Three-step wizard:
 *   1. Pick product kind + download template (or skip if user already has a CSV).
 *   2. Upload the CSV, review auto-detected column mapping, adjust as needed,
 *      re-preview to see per-row validation.
 *   3. Commit approved rows; show summary of created + skipped.
 */
const AdminProductImport = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [kinds, setKinds] = useState([]);
  const [selectedKind, setSelectedKind] = useState('school_uniform');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/products/import/kinds`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setKinds(r.data || [])).catch(() => setKinds([]));
  }, [token]);

  const activeKind = kinds.find((k) => k.kind === selectedKind);

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products/import/template/${selectedKind}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kyis-${selectedKind}-template.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download template');
    }
  };

  const runPreview = async (mappingOverride) => {
    if (!file) { toast.error('Please upload a CSV first'); return; }
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('kind', selectedKind);
      fd.append('file', file);
      if (mappingOverride) fd.append('column_mapping', JSON.stringify(mappingOverride));
      const res = await axios.post(`${API_URL}/api/products/import/preview`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
      setResult(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse CSV');
    } finally {
      setPreviewing(false);
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
  };

  const handleMappingChange = (csvHeader, newFieldKey) => {
    if (!preview) return;
    const nextMapping = { ...preview.auto_mapping };
    if (!newFieldKey) delete nextMapping[csvHeader];
    else nextMapping[csvHeader] = newFieldKey;
    runPreview(nextMapping);
  };

  const handleCommit = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter((r) => r.errors.length === 0).map((r) => r.normalized);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }
    setCommitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/products/import/commit`, {
        kind: selectedKind,
        rows: validRows,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
      toast.success(`Imported ${res.data.created_count} products`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="w-full min-w-0 px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-product-import">
      <Link to="/admin/products" className="inline-flex items-center text-sm text-gray-500 hover:text-[#1B3A5C] mb-4">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Products
      </Link>
      <h1 className="text-2xl font-bold text-[#1B3A5C] mb-1">Import Products from CSV</h1>
      <p className="text-gray-500 mb-8">
        Bulk-load products from a spreadsheet. Pick a template, upload your CSV, adjust column mappings, review each row, then import.
      </p>

      <div className="bg-[#4A90A4]/5 border border-[#4A90A4]/20 rounded-2xl p-4 mb-6 text-sm text-[#1B3A5C]">
        <p className="font-bold mb-1">Product images in bulk uploads</p>
        <p className="text-gray-600">
          Use the <span className="font-mono text-[#1B3A5C]">Product Image URL(s)</span> column in the template. Paste publicly accessible image URLs, separated by commas, semicolons, or pipes.
          The first URL becomes the primary product image. Imported URLs are copied into first-party asset storage before being saved.
        </p>
      </div>

      {/* Step 1: Kind selector + template download */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6" data-testid="import-step-1">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#4A90A4] mb-4">Step 1 — Pick product kind &amp; grab template</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {kinds.map((k) => (
            <button
              key={k.kind}
              type="button"
              onClick={() => { setSelectedKind(k.kind); setPreview(null); setResult(null); }}
              className={`text-left px-4 py-3 rounded-xl border transition-colors ${selectedKind === k.kind ? 'border-[#F5A623] bg-[#F5A623]/5 ring-2 ring-[#F5A623]/20' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              data-testid={`import-kind-${k.kind}`}
            >
              <div className="text-sm font-bold text-[#1B3A5C]">{k.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {k.fields.filter((f) => f.required).length} required · {k.fields.length} total fields
              </div>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1B3A5C] text-white text-sm font-semibold rounded-lg hover:brightness-110 transition-all"
          data-testid="download-template-btn"
        >
          <Download className="w-4 h-4" /> Download {activeKind?.label} template
        </button>

        {activeKind && (
          <details className="mt-4 text-xs text-gray-500 group">
            <summary className="cursor-pointer font-semibold text-gray-600 hover:text-gray-800">
              Show expected columns for {activeKind.label}
            </summary>
            <ul className="mt-3 space-y-1.5">
              {activeKind.fields.map((f) => (
                <li key={f.key} className="flex items-start gap-2">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${f.required ? 'bg-red-500' : 'bg-gray-300'}`} />
                  <div>
                    <span className="font-mono text-gray-800">{f.label}</span>
                    {f.required && <span className="ml-1.5 text-[10px] uppercase font-bold text-red-500">required</span>}
                    {f.hint && <span className="ml-2 text-gray-400 italic">{f.hint}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* Step 2: Upload CSV */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6" data-testid="import-step-2">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#4A90A4] mb-4">Step 2 — Upload your CSV</h2>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-[#4A90A4] text-[#4A90A4] text-sm font-semibold rounded-lg hover:bg-[#4A90A4]/5 transition-all"
            data-testid="upload-csv-btn"
          >
            <Upload className="w-4 h-4" /> {file ? 'Choose different file' : 'Choose CSV file'}
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
          {file && (
            <>
              <span className="text-sm text-gray-600 font-medium">{file.name}</span>
              <button
                type="button"
                onClick={() => runPreview()}
                disabled={previewing}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#F5A623] text-white text-sm font-bold rounded-lg hover:brightness-110 disabled:opacity-50 transition-all"
                data-testid="parse-csv-btn"
              >
                {previewing ? 'Parsing...' : 'Parse & preview'}
              </button>
            </>
          )}
        </div>
      </section>

      {/* Step 3: Column mapping + row preview */}
      {preview && !result && (
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6" data-testid="import-step-3">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#4A90A4]">Step 3 — Review &amp; map</h2>
            <div className="flex items-center gap-3 text-xs">
              <StatChip icon={CheckCircle} tone="green" count={preview.valid_rows} label="valid" />
              <StatChip icon={AlertTriangle} tone="amber" count={preview.warning_rows} label="warnings" />
              <StatChip icon={XCircle} tone="red" count={preview.error_rows} label="errors" />
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Column mapping</h3>
            <p className="text-xs text-gray-500 mb-3">
              Map each CSV column to a product field. Unmapped columns are ignored.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {preview.headers.map((h) => (
                <div key={h} className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 font-mono truncate flex-1" title={h}>{h}</span>
                  <span className="text-gray-300">→</span>
                  <select
                    value={preview.auto_mapping[h] || ''}
                    onChange={(e) => handleMappingChange(h, e.target.value)}
                    className="text-sm px-2.5 py-1.5 border border-gray-200 rounded-lg bg-white flex-1 focus:outline-none focus:ring-2 focus:ring-[#4A90A4]/20"
                    data-testid={`mapping-${h}`}
                  >
                    <option value="">— ignore —</option>
                    {preview.template.map((f) => (
                      <option key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Row preview</h3>
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Row</th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Status</th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Name</th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Price</th>
                  <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Details</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <RowRow key={i} idx={i + 1} row={r} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={handleCommit}
              disabled={committing || preview.valid_rows === 0}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#F5A623] text-white text-sm font-bold rounded-lg hover:brightness-110 disabled:opacity-50 transition-all"
              data-testid="commit-import-btn"
            >
              {committing ? 'Importing...' : `Import ${preview.valid_rows} valid row${preview.valid_rows === 1 ? '' : 's'}`}
            </button>
          </div>
        </section>
      )}

      {/* Step 4: Result */}
      {result && (
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="import-result">
          <h2 className="text-lg font-bold text-[#1B3A5C] mb-4">Import complete</h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 rounded-xl p-4 border border-green-100">
              <div className="text-3xl font-bold text-green-700">{result.created_count}</div>
              <div className="text-xs uppercase tracking-wider text-green-800 mt-1">Created</div>
            </div>
            <div className="bg-red-50 rounded-xl p-4 border border-red-100">
              <div className="text-3xl font-bold text-red-700">{result.skipped_count}</div>
              <div className="text-xs uppercase tracking-wider text-red-800 mt-1">Skipped</div>
            </div>
          </div>
          {result.skipped_count > 0 && (
            <>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Skipped rows</h3>
              <ul className="mb-6 space-y-1.5">
                {result.skipped.map((s) => (
                  <li key={s.row} className="text-sm text-gray-700">
                    <span className="font-mono text-gray-400">Row {s.row}:</span> {s.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1B3A5C] text-white text-sm font-bold rounded-lg hover:brightness-110 transition-all"
              data-testid="view-products-btn"
            >
              View products
            </button>
            <button
              type="button"
              onClick={() => { setPreview(null); setResult(null); setFile(null); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-200 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-all"
            >
              Import another file
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

const StatChip = ({ icon: Icon, tone, count, label }) => {
  const toneClasses = {
    green: 'bg-green-50 text-green-700 border-green-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    red:   'bg-red-50 text-red-700 border-red-100',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${toneClasses}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="font-bold">{count}</span> {label}
    </span>
  );
};

const RowRow = ({ idx, row }) => {
  const hasError = row.errors.length > 0;
  const hasWarning = row.warnings.length > 0;
  const Status = hasError ? XCircle : hasWarning ? AlertTriangle : CheckCircle;
  const statusTone = hasError ? 'text-red-600' : hasWarning ? 'text-amber-600' : 'text-green-600';
  return (
    <tr className={`border-b border-gray-50 ${hasError ? 'bg-red-50/30' : hasWarning ? 'bg-amber-50/20' : ''}`}>
      <td className="px-3 py-2.5 text-xs text-gray-400 font-mono">{idx}</td>
      <td className="px-3 py-2.5">
        <Status className={`w-4 h-4 ${statusTone}`} />
      </td>
      <td className="px-3 py-2.5 text-gray-800 font-medium">{row.normalized?.name || <span className="text-gray-300 italic">—</span>}</td>
      <td className="px-3 py-2.5 text-gray-700">{row.normalized?.base_price != null ? `$${Number(row.normalized.base_price).toFixed(2)}` : <span className="text-gray-300 italic">—</span>}</td>
      <td className="px-3 py-2.5">
        {row.errors.map((e, i) => (
          <div key={`e${i}`} className="text-xs text-red-700 flex items-center gap-1.5"><XCircle className="w-3 h-3 flex-shrink-0" /> {e}</div>
        ))}
        {row.warnings.map((w, i) => (
          <div key={`w${i}`} className="text-xs text-amber-700 flex items-center gap-1.5"><Info className="w-3 h-3 flex-shrink-0" /> {w}</div>
        ))}
        {!hasError && !hasWarning && <span className="text-xs text-green-600">Ready to import</span>}
      </td>
    </tr>
  );
};

export default AdminProductImport;
